import React, { useState } from 'react';
import { Edit2, Save, X, DollarSign, Percent } from 'lucide-react';
import './PricingAdjustmentsCard.css';

const PricingAdjustmentsCard = ({ adjustments, onUpdate }) => {
  const [editingAdjustment, setEditingAdjustment] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [updateReason, setUpdateReason] = useState('');

  const startEdit = (adjustment) => {
    setEditingAdjustment(adjustment.id);
    setEditValue(adjustment.current_value);
    setUpdateReason('');
  };

  const cancelEdit = () => {
    setEditingAdjustment(null);
    setEditValue('');
    setUpdateReason('');
  };

  const saveEdit = async () => {
    if (!updateReason.trim()) {
      alert('Please provide a reason for this pricing change');
      return;
    }

    try {
      await onUpdate(editingAdjustment, { current_value: parseFloat(editValue) }, updateReason);
      cancelEdit();
    } catch (error) {
      console.error('Error saving adjustment:', error);
      alert('Failed to save pricing adjustment');
    }
  };

  const formatValue = (adjustment) => {
    if (adjustment.adjustment_type === 'percentage') {
      return `${adjustment.current_value}%`;
    }
    return `$${adjustment.current_value?.toLocaleString()}`;
  };

  const getDescription = (adjustment) => {
    switch (adjustment.name) {
      case 'Linework':
        return 'Linework-only projects';
      case 'Erosion Surface':
        return 'Add-on for erosion modeling';
      case 'Advanced Utilities':
        return 'Per structure';
      case 'Utilities':
        return 'Of base cost';
      default:
        return adjustment.unit_label || '';
    }
  };

  return (
    <div className="adjustments-grid">
      {adjustments.map((adjustment) => {
        const isEditing = editingAdjustment === adjustment.id;
        const isPercentage = adjustment.adjustment_type === 'percentage';

        return (
          <div key={adjustment.id} className={`adjustment-card ${isEditing ? 'editing' : ''}`}>
            {isEditing ? (
              <div className="adjustment-edit-form">
                <div className="adjustment-edit-header">
                  <span className="adjustment-edit-name">{adjustment.name}</span>
                </div>

                <div className="adjustment-edit-field">
                  <label>{isPercentage ? 'Percentage' : 'Amount'}</label>
                  <div className="adjustment-input-wrapper">
                    {!isPercentage && <span className="input-prefix">$</span>}
                    <input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      step={isPercentage ? '0.5' : '10'}
                      autoFocus
                    />
                    {isPercentage && <span className="input-suffix">%</span>}
                  </div>
                </div>

                <div className="adjustment-edit-field">
                  <label>Reason <span className="required">*</span></label>
                  <input
                    type="text"
                    value={updateReason}
                    onChange={(e) => setUpdateReason(e.target.value)}
                    placeholder="e.g., Market adjustment"
                  />
                </div>

                <div className="adjustment-edit-actions">
                  <button className="adj-btn save" onClick={saveEdit}>
                    <Save size={14} />
                    Save
                  </button>
                  <button className="adj-btn cancel" onClick={cancelEdit}>
                    <X size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="adjustment-icon">
                  {isPercentage ? <Percent size={18} /> : <DollarSign size={18} />}
                </div>
                <div className="adjustment-info">
                  <span className="adjustment-name">{adjustment.name}</span>
                  <span className="adjustment-desc">{getDescription(adjustment)}</span>
                </div>
                <div className="adjustment-value">
                  {formatValue(adjustment)}
                </div>
                <button
                  className="adjustment-edit-btn"
                  onClick={() => startEdit(adjustment)}
                  title="Edit"
                >
                  <Edit2 size={14} />
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PricingAdjustmentsCard;
